import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Send,
  MessageSquare,
  Bot,
  User,
  Trash2,
  Loader2,
  Crown,
  Apple
} from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const AIDietitianChat = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const messagesEndRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [statusRes, historyRes] = await Promise.all([
        axios.get(`${API}/ai/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ai/chat-history`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStatus(statusRes.data);
      setMessages(historyRes.data.messages || []);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    // Optimistically add user message
    setMessages(prev => [...prev, {
      role: 'user',
      text: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai/chat`,
        { message: userMessage, language: i18n.language },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add assistant response
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: response.data.response,
        timestamp: response.data.timestamp
      }]);

      // Update status for remaining messages
      if (!status?.is_premium) {
        setStatus(prev => ({
          ...prev,
          limits: {
            ...prev.limits,
            chat: {
              ...prev.limits.chat,
              remaining: Math.max(0, (prev.limits.chat.remaining || 0) - 1)
            }
          }
        }));
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Mesaj limitinize ulaştınız');
        // Remove optimistic message
        setMessages(prev => prev.slice(0, -1));
      } else {
        toast.error('Mesaj gönderilemedi');
        setMessages(prev => prev.slice(0, -1));
      }
    } finally {
      setSending(false);
    }
  };

  const handleClearHistory = async () => {
    if (!confirm('Sohbet geçmişinizi silmek istediğinize emin misiniz?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/ai/chat-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages([]);
      toast.success('Sohbet geçmişi temizlendi');
    } catch (error) {
      toast.error('Geçmiş temizlenemedi');
    }
  };

  const suggestedQuestions = [
    'Günlük protein ihtiyacım ne kadar?',
    'Kahvaltıda ne yemeli?',
    'Kilo vermek için ne yapmalıyım?',
    'Sağlıklı atıştırmalıklar önerir misin?'
  ];

  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6 h-[calc(100vh-80px)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/ai-dietitian')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          
          <div className="flex items-center gap-3">
            {!status?.is_premium && (
              <Badge variant="outline" className="text-orange-600 border-orange-300">
                {status?.limits?.chat?.remaining || 0} mesaj kaldı
              </Badge>
            )}
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-700"
                onClick={handleClearHistory}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Chat Header Card */}
        <Card className="mb-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white">
          <CardContent className="p-4 flex items-center">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
              <Apple className="h-6 w-6" />
            </div>
            <div>
              <h2 className="font-semibold">AI Diyetisyen</h2>
              <p className="text-sm text-white/80">Beslenme, diyet ve sağlıklı yaşam hakkında sorularınızı sorun</p>
            </div>
          </CardContent>
        </Card>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto bg-white rounded-lg border p-4 mb-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500">
              <MessageSquare className="h-16 w-16 text-gray-300 mb-4" />
              <p className="mb-6">Henüz bir sohbet başlamadı</p>
              
              {/* Suggested Questions */}
              <div className="w-full max-w-md">
                <p className="text-sm text-gray-400 mb-3 text-center">Önerilen sorular:</p>
                <div className="grid grid-cols-2 gap-2">
                  {suggestedQuestions.map((q, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      size="sm"
                      className="text-xs h-auto py-2 px-3 text-left"
                      onClick={() => setInput(q)}
                    >
                      {q}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-start max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <Avatar className={`h-8 w-8 ${msg.role === 'user' ? 'ml-2' : 'mr-2'}`}>
                      <AvatarFallback className={msg.role === 'user' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                        {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                      </AvatarFallback>
                    </Avatar>
                    <div
                      className={`rounded-lg px-4 py-2 ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <p className="whitespace-pre-wrap text-sm">{msg.text}</p>
                      <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-blue-200' : 'text-gray-400'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="flex items-center bg-gray-100 rounded-lg px-4 py-2">
                    <Loader2 className="h-4 w-4 animate-spin mr-2 text-green-600" />
                    <span className="text-sm text-gray-500">Diyetisyen yanıtlıyor...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <form onSubmit={handleSend} className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Mesajınızı yazın..."
            className="flex-1"
            disabled={sending || (!status?.is_premium && status?.limits?.chat?.remaining <= 0)}
          />
          <Button
            type="submit"
            className="bg-green-600 hover:bg-green-700"
            disabled={sending || !input.trim() || (!status?.is_premium && status?.limits?.chat?.remaining <= 0)}
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>

        {/* Premium Upgrade Prompt */}
        {!status?.is_premium && status?.limits?.chat?.remaining <= 0 && (
          <Card className="mt-4 bg-yellow-50 border-yellow-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center">
                <Crown className="h-5 w-5 text-yellow-600 mr-2" />
                <span className="text-sm">Günlük mesaj limitinize ulaştınız</span>
              </div>
              <Button
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-600 text-white"
                onClick={() => navigate('/premium')}
              >
                Premium'a Geç
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AIDietitianChat;
